import { EmbedAppContentKind } from "client/Api/Apps/EditApp/saveApp";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	RefObject,
	useLayoutEffect,
	useRef
} from "react";
import { UserHtml } from "../UserHtml/UserHtml";
import styles from "./EmbedAppContent.module.less";
import {
	ensureNotNil,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { applyTableStylesToViewer } from "client/Components/RichTextEditor/tableRendering";
import { RawHtml } from "client/Components";

export type EmbedAppContentProps = {
	kind: EmbedAppContentKind;
	html: string;
	trackingId: string;
};

export const EmbedAppContent = (props: EmbedAppContentProps): ReactElement => {
	const {
		kind,
		html,
		trackingId
	} = props;

	const renderContent = (): ReactElement => {
		switch (kind) {
			case EmbedAppContentKind.EmbedCode:
				return (
					<UserHtml
						html={html}
						trackingId="embed-code"
						size="stretch"
					/>
				);
			case EmbedAppContentKind.CustomHtml:
				return (
					<CustomHtml
						html={html}
						trackingId="custom-html"
					/>
				);
			default:
				never(kind);
		}
	};

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{renderContent()}
		</div>
	);
};
EmbedAppContent.displayName = "EmbedAppContent";

type CustomHtmlProps = {
	html: string;
	trackingId: string;
};

const CustomHtml = (props: CustomHtmlProps): ReactElement => {
	const {
		html,
		trackingId
	} = props;

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			hasImplicitDependencyOn(html);

			const container: HTMLDivElement = ensureNotNil(ref.current);
			applyTableStylesToViewer(container);
		},
		[html]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			ref={ref}
			className={styles.customHtml}
		>
			<RawHtml html={html} />
		</div>
	);
};
CustomHtml.displayName = "CustomHtml";
